/**
 * The transport as a hosted plugin hears it.
 *
 * A sequencer, an arpeggiator or a tempo-synced delay needs to know whether
 * the transport is playing, where it is and how fast. The format's answer is
 * a `wam-transport` event: the bar the transport is in, the context time that
 * bar started, the tempo and the meter, and a `playing` flag. A plugin works
 * everything else out from those, so it has to be told again whenever any of
 * them changes: at the start of a pass, at every bar line, and at every tempo
 * or meter event inside the pass.
 *
 * Pure data, like the scheduling plan: the engine and the offline render turn
 * these into events on the plugins' nodes, each on its own clock.
 */
import type { Project } from '@/domain/types';
import { signatureMapOf, tempoMapOf } from '@/domain/selectors';
import { secondsToTicks, tempoAt, ticksToBarBeat, ticksToSeconds, signatureAt, barBeatToTicks } from '@/timing/tempoMap';

export interface TransportTell {
  /** Context time the event is due. */
  time: number;
  currentBar: number;
  currentBarStarted: number;
  tempo: number;
  timeSigNumerator: number;
  timeSigDenominator: number;
  playing: boolean;
}

/** Ticks → context time, or null for a moment the timeline does not cover. */
export type TimeResolver = (projectSeconds: number) => number | null;

/**
 * Everything a plugin has to be told between two moments of a playing pass:
 * one tell at `fromSec`, and one at every bar line, tempo event and meter
 * event before `toSec`. A pass with no end — a non-looping play — is told as
 * far as `horizonSec` ahead.
 */
export function transportTells(project: Project, fromSec: number, toSec: number, timeFor: TimeResolver, horizonSec = 60): TransportTell[] {
  const tempo = tempoMapOf(project);
  const meter = signatureMapOf(project);
  const end = Number.isFinite(toSec) ? toSec : fromSec + horizonSec;
  const fromTick = secondsToTicks(tempo, Math.max(0, fromSec));
  const toTick = secondsToTicks(tempo, Math.max(end, fromSec));

  // The moments that change the answer: the start, every bar line, and every
  // tempo or meter event, in tick order without repeats.
  const ticks = new Set<number>([fromTick]);
  for (const event of tempo.events) if (event.tick > fromTick && event.tick < toTick) ticks.add(event.tick);
  for (const event of meter.events) if (event.tick > fromTick && event.tick < toTick) ticks.add(event.tick);
  let { bar } = ticksToBarBeat(meter, fromTick);
  for (;;) {
    const next = barBeatToTicks(meter, bar + 1);
    if (next >= toTick) break;
    ticks.add(next);
    bar += 1;
  }

  const tells: TransportTell[] = [];
  for (const tick of [...ticks].sort((a, b) => a - b)) {
    const time = timeFor(ticksToSeconds(tempo, tick));
    if (time === null) continue;
    const position = ticksToBarBeat(meter, tick);
    const barStartTick = barBeatToTicks(meter, position.bar);
    const barStarted = timeFor(ticksToSeconds(tempo, barStartTick));
    const sig = signatureAt(meter, tick);
    tells.push({
      time,
      currentBar: position.bar,
      // A bar that started before the pass did has no context time of its
      // own; it is placed by the tempo, back from where the pass begins.
      currentBarStarted: barStarted ?? time - (ticksToSeconds(tempo, tick) - ticksToSeconds(tempo, barStartTick)),
      tempo: tempoAt(tempo, tick),
      timeSigNumerator: sig.numerator,
      timeSigDenominator: sig.denominator,
      playing: true,
    });
  }
  return tells;
}

/** The tell for a transport that has stopped, at a context time. */
export function transportStopped(project: Project, atTick: number, time: number): TransportTell {
  const tempo = tempoMapOf(project);
  const meter = signatureMapOf(project);
  const tick = Math.max(0, atTick);
  const sig = signatureAt(meter, tick);
  return {
    time,
    currentBar: ticksToBarBeat(meter, tick).bar,
    currentBarStarted: time,
    tempo: tempoAt(tempo, tick),
    timeSigNumerator: sig.numerator,
    timeSigDenominator: sig.denominator,
    playing: false,
  };
}
